import EmptyResultsRow from 'components/EmptyResultsRow';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { List, OrderedMap } from 'immutable';
import React, { lazy, ReactElement } from 'react';
import { Interaction } from 'records/interactions';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { RFI } from 'records/RFI';
import { SheetMap } from 'records/Sheet';
import RFIRow from './RFIRow';

const ExpandableTableRow = lazy(
  () => import('components/ExpandableTableRow/ExpandableTableRow')
);

// Once the LaunchDarkly flag is removed, the only prop needed will be queriedRFIGroups
interface Props {
  rfis: OrderedMap<string, RFI>;
  queriedRFIs: List<string>;
  queriedRFIGroups: OrderedMap<string, List<RFI>>;
  interactions: List<Interaction>;
  sheets: SheetMap;
}

const RFIListTable = (props: Props) => {
  const { queriedRFIs, queriedRFIGroups, rfis, interactions, sheets } = props;
  const { [FeatureFlag.RfiRevisions]: isRfiRevisioningEnabled } =
    useFeatureFlags();

  const qRFIs = queriedRFIs
    .map((rfiid: string): RFI => rfis.get(rfiid, new RFI()))
    .filter((rfi: RFI): boolean => rfi.id !== '');

  const currentlyOpenViewRFI = interactions.some((i) => i.type === 'ViewRFI')
    ? (interactions.find((i) => i.type === 'ViewRFI') as ViewRFI)
    : new ViewRFI();

  const openRfi = currentlyOpenViewRFI.rfi.id;

  const template =
    queriedRFIGroups.size > 0 ? (
      queriedRFIGroups.valueSeq().map((rfis: List<RFI>): React.ReactElement => {
        const rowId = Math.floor(Math.random() * 1000000);
        return (
          <ExpandableTableRow
            key={`expandable-table-row-${rowId}`}
            data={rfis}
            rowHeight={65}
            sheets={sheets}
          />
        );
      })
    ) : (
      <EmptyResultsRow type="search" />
    );

  const originalTemplate =
    qRFIs.size > 0 ? (
      qRFIs.map((rfi: RFI): React.ReactNode => {
        return <RFIRow key={rfi.id} rfi={rfi} openItemId={openRfi} />;
      })
    ) : (
      <EmptyResultsRow type="search" />
    );

  return isRfiRevisioningEnabled
    ? (template as ReactElement)
    : (originalTemplate as ReactElement);
};

function areEqual(
  prevProps: {
    queriedRFIGroups: OrderedMap<string, List<RFI>>;
    sheets: SheetMap;
  },
  nextProps: {
    queriedRFIGroups: OrderedMap<string, List<RFI>>;
    sheets: SheetMap;
  }
): boolean {
  if (prevProps === nextProps) return true;
  if (prevProps === null || nextProps === null) return false;
  if (prevProps.queriedRFIGroups.size !== nextProps.queriedRFIGroups.size)
    return false;
  if (prevProps.sheets.size !== nextProps.sheets.size) return false;

  for (let i = 0; i < prevProps.queriedRFIGroups.size; ++i) {
    if (
      prevProps.queriedRFIGroups.valueSeq().get(i) !==
      nextProps.queriedRFIGroups.valueSeq().get(i)
    )
      return false;
  }

  for (let i = 0; i < prevProps.sheets.size; ++i) {
    if (
      prevProps.sheets.valueSeq().get(i) !== nextProps.sheets.valueSeq().get(i)
    )
      return false;
  }

  return true;
}

const memoizedRFIList = React.memo(RFIListTable, areEqual);

export default memoizedRFIList;
